import { useState } from 'react';
import { Spin, Empty, Image, Modal, Input } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import { $ } from '../../lib/api';
// ux9：日期统一出口
import { fmtDateTime, fmtMonthDayTime, fmtDateShort, fmtRelative } from '../../lib/format';
import { useHasPermission } from '../../lib/permissions'; // P1-3 删客户按钮权限位
import {
  PageHeader, Btn, Pill, AiBox, JourneyCta, STAGE_META, STATUS_META, avatarSlot, Avatar, healthLevel,
} from '../../components/ui';
import { JourneyModal } from '../../components/JourneyModal';
import { ChatAnalysisModal } from '../../components/ChatAnalysisModal';
import { SuggestMessageModal } from '../../components/SuggestMessageModal';
import { TransferCustomerModal } from '../../components/TransferCustomerModal';
import { StageSopCard } from '../../components/StageSopCard';
import { L0StageCard } from '../../components/L0StageCard';
import { WechatProofModal } from '../../components/WechatProofModal';
import { DaysSinceChip, CountdownChip } from '../../components/CountdownChip';
import { SalesFlowProgress } from '../../components/SalesFlowProgress';
// 第2批纯前端合页：收款/发票合一卡 + 履约（教材/资料/订单）三段卡，挂主列进度条正下方
import { PaymentInvoiceCard } from '../../components/flow/PaymentInvoiceCard';
import { FulfillmentCard } from '../../components/flow/FulfillmentCard';
import { useMutation } from '@tanstack/react-query';
import { message } from 'antd';

/** P2-1: AI 画像 markdown 渲染器
 *  之前 summary 是 raw markdown 字符串（**粗体** / # 标题），直接当文本塞进 AiBox
 *  导致用户看到一堆 # 和 ** 符号。react-markdown 内置 sanitize，不需要额外配置。
 *  组件作用域限定在 .ai-md（CSS 在 AiBox 内部）以免污染全局排版。 */
function AiMd({ text }: { text?: string | null }) {
  if (!text) return null;
  return (
    <div className="ai-md">
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
}

const AVATAR_BG: Record<number, string> = {
  1: 'linear-gradient(135deg, #6294f5, #3370ff)',
  2: 'linear-gradient(135deg, #51cf8a, #34c759)',
  3: 'linear-gradient(135deg, #d6649e, #c44569)',
  4: 'linear-gradient(135deg, #f6c155, #f0a020)',
  5: 'linear-gradient(135deg, #9b7dd6, #7c4dff)',
  6: 'linear-gradient(135deg, #ee7065, #ee5253)',
  7: 'linear-gradient(135deg, #5eb6e0, #3aafda)',
  8: 'linear-gradient(135deg, #95cc6c, #76b639)',
};

export function CustomerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const cid = Number(id);
  const canDelete = useHasPermission('customer:delete'); // P1-3：仅 admin/director 显删除客户
  const [tab, setTab] = useState<'detail' | 'timeline' | 'business' | 'attachments'>('detail');
  const [showJourney, setShowJourney] = useState(false);
  // P1-2: AI 解析聊天记录弹框
  const [showChatAnalyze, setShowChatAnalyze] = useState(false);
  // P2-1: 智能话术推荐弹框
  const [showSuggest, setShowSuggest] = useState(false);
  // P2-10: 转客户弹框
  const [showTransfer, setShowTransfer] = useState(false);
  // A2: 加微信「通过截图」补传/查看弹框（L1+ 客户用，L0 走 L0StageCard）
  const [showProof, setShowProof] = useState(false);
  // ux1：「📱标记被删」按钮布尔防重（裸 async onClick、Btn 仅支持 disabled）
  const [markingDeleted, setMarkingDeleted] = useState(false);
  // ux6：删客户强确认——需输入客户姓名比对才放行
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [delConfirmText, setDelConfirmText] = useState('');
  // 第2批合页联动：进度条节点点击 → 记当前激活段 key + 滚动定位到主列对应卡段（卡内据此展开）
  const [activeStepKey, setActiveStepKey] = useState<string | undefined>(undefined);
  const onStepClick = (key: string, route?: string) => {
    // 有合页锚点段（5_deposit/4_invoice/6_shipment/7_materials/8_order）→ 滚动定位 + 记激活段
    // 无锚点的 1/2/3 步（签合同前）→ 退化跳对应列表（修 no-op：原来点这些节点没反应）
    const el = document.getElementById('flow-sec-' + key);
    if (el) {
      setActiveStepKey(key);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (route) {
      nav(route + '?customerId=' + cid);
    }
  };
  const qc = useQueryClient();

  const { data: c, isLoading } = useQuery({
    queryKey: ['customer', String(cid)],
    queryFn: () => $.get<any>(`/customers/${cid}`),
  });
  const { data: follows } = useQuery({
    queryKey: ['follow-ups', cid],
    queryFn: () => $.get<{ items: any[] }>(`/follow-ups`, { customerId: cid, pageSize: 50 }),
  });
  // P1-B: 客户附件（懒加载，tab 切到时再查）
  const { data: attachments } = useQuery({
    queryKey: ['customer-attachments', cid],
    queryFn: () => $.get<any[]>(`/customers/${cid}/attachments`),
    enabled: !!cid,
  });
  const attachmentCount = attachments?.length ?? 0;

  if (isLoading) return <Spin size="large" style={{ margin: '120px auto', display: 'block' }} />;
  if (!c) return <Empty />;

  // ux6：已删除客户(已退L0回公海、owner已清)隐藏写状态/归属/建业务的操作按钮
  const isDeleted = c.status === 'deleted';

  const stage = STAGE_META[c.currentStage] ?? { tone: 'default' as const, label: c.currentStage };
  const status = STATUS_META[c.status] ?? { tone: 'default' as const, label: c.status };
  const slot = avatarSlot(c.id);
  const followItems = follows?.items ?? [];
  const recent = followItems.slice(0, 6);

  return (
    <>
      <PageHeader
        breadcrumb={['客户管理', c.nickname]}
        title={c.nickname}
        tag={stage.label}
        subtitle={`健康度 ${c.healthScore ?? '—'}`}
        actions={
          <>
            {/* ux6：deleted 客户隐藏改归属/建业务的破坏性操作，只读操作保留 */}
            {!isDeleted && (
              <Btn kind="default" onClick={() => setShowTransfer(true)}>转给同事</Btn>
            )}
            <Btn kind="default" onClick={() => setShowSuggest(true)}>AI 推荐话术</Btn>
            <Btn kind="default" onClick={() => setShowChatAnalyze(true)}>AI 解析聊天</Btn>
            {!isDeleted && (
              <Btn kind="default" onClick={async () => {
                try {
                  const conv: any = await $.post('/im/business-conv', {
                    entity_type: 'customer',
                    entity_id: Number(cid),
                    entity_name: c.realName ?? c.nickname ?? `#${cid}`,
                    member_user_ids: c.ownerId ? [Number(c.ownerId)] : [],
                  });
                  nav(`/im?conv=${conv.id}`);
                } catch (e: any) {
                  message.error(e?.message ?? '打开讨论失败');
                }
              }}>💬 讨论</Btn>
            )}
            {!isDeleted && (
              <Btn kind="default" onClick={() => nav(`/follow-ups/new?customer=${cid}`)}>新增跟进</Btn>
            )}
            {/* v1.5 批3 决策1③：销售端"申请作废" — 提交后主管审批，通过则进公海 */}
            {!isDeleted && (
              <Btn kind="default" onClick={async () => {
                const reason = window.prompt('请填写作废原因（必填，主管审批用）：');
                if (!reason || !reason.trim()) return;
                try {
                  await $.post('/customer-voidage/request', {
                    customer_id: Number(cid),
                    reason: reason.trim(),
                  });
                  message.success('已提交作废申请，主管审批后客户将进入公海');
                } catch (e: any) {
                  message.error(e?.response?.data?.message ?? e?.message ?? '提交失败');
                }
              }}>📤 申请作废</Btn>
            )}
            {/* 大流程批7：销售标记"客户把我微信删了"→填原因→主管硬审→通过标deleted+退L0+回公海
                ux1：裸 async onClick 无 mutation，用 markingDeleted 布尔最小防重 */}
            {!isDeleted && (
              <Btn kind="default" disabled={markingDeleted} onClick={async () => {
                const map: Record<string, string> = { '1': 'customer_deleted', '2': 'no_interest', '3': 'wrong_add', '4': 'wrong_info' };
                const pick = window.prompt('客户把你微信删了？选删除原因(输数字 1-4)：\n1=客户主动删/拉黑   2=客户无意向   3=误加   4=信息填错');
                if (pick === null) return;
                const deletionReason = map[pick.trim()];
                if (!deletionReason) { message.error('请输入 1-4'); return; }
                const text = window.prompt('补充说明（可选，可留空）：') ?? '';
                setMarkingDeleted(true);
                try {
                  await $.post('/customer-deletion/request', {
                    customerId: Number(cid), deletionReason, deletionReasonText: text.trim() || undefined,
                  });
                  message.success('已提交删除申请，主管审批通过后客户退回 L0 并回公海');
                } catch (e: any) {
                  message.error(e?.response?.data?.message ?? e?.message ?? '提交失败');
                } finally {
                  setMarkingDeleted(false);
                }
              }}>📱 标记被删</Btn>
            )}
            {/* A2: L1+ 客户补传/查看加微信通过截图（L0 走 L0StageCard 的确认流程） */}
            {c.currentStage !== 'L0' && !isDeleted && (
              <Btn kind="default" onClick={() => setShowProof(true)}>📎 通过截图</Btn>
            )}
            {canDelete && !isDeleted && (
              <Btn kind="default" onClick={() => { setDelConfirmText(''); setShowDeleteConfirm(true); }}>🗑 删除客户</Btn>
            )}
            <Btn kind="primary" onClick={() => setShowJourney(true)}>查看完整旅程</Btn>
          </>
        }
      />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div className="work-content" style={{ flex: 1 }}>
          {c.currentStage === 'L0' && (
            <L0StageCard customer={c} onChanged={() => qc.invalidateQueries({ queryKey: ['customer', String(cid)] })} />
          )}
          {/* v1.2 Batch 2-A：销售流程进度条 + L7 决策横幅 */}
          <SalesFlowProgress
            customerId={cid}
            currentStage={c.currentStage}
            salesFlowStep={c.salesFlowStep}
            depositChosen={c.depositChosen}
            contractApprovalStatus={c.contractApprovalStatus}
            contractRejectionReason={c.contractRejectionReason}
            onRefresh={() => qc.invalidateQueries({ queryKey: ['customer', String(cid)] })}
            onStepClick={onStepClick}
          />
          {/* 第2批纯前端合页：收款/发票合一卡 + 履约（教材/资料/订单）三段卡，挂进度条正下方主列。
              仅【已启动销售流程】（有 salesFlowStep）才显合页；L1-L6 未启动销售流程的客户不显空灰卡。
              onRefresh 拉详情即时刷新徽章/金额；activeStepKey 由进度条点击联动展开+滚动。 */}
          {c.salesFlowStep && (
            <>
              <PaymentInvoiceCard
                customerId={cid}
                customerName={c.realName ?? c.nickname}
                salesFlowStep={c.salesFlowStep}
                activeStepKey={activeStepKey}
                onRefresh={() => qc.invalidateQueries({ queryKey: ['customer', String(cid)] })}
              />
              <FulfillmentCard
                customerId={cid}
                salesFlowStep={c.salesFlowStep}
                depositChosen={c.depositChosen}
                activeStepKey={activeStepKey}
                onRefresh={() => qc.invalidateQueries({ queryKey: ['customer', String(cid)] })}
              />
            </>
          )}
          {/* P6-3 加微天数 + 下次跟进倒计时（L1+ 客户显示） */}
          {c.currentStage !== 'L0' && (c.wechatAddedAt || c.nextFollowupDueAt) && (
            <div style={{
              display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap',
              padding: '12px 16px', marginBottom: 16,
              background: 'var(--card-bg)', border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-sm)',
            }}>
              {c.wechatAddedAt && <DaysSinceChip date={c.wechatAddedAt} label="加微时间" />}
              {c.nextFollowupDueAt && <CountdownChip dueAt={c.nextFollowupDueAt} label="下次跟进截止" />}
            </div>
          )}
          <JourneyCta
            title={`${c.nickname} 的完整旅程`}
            sub={`从 L1 触达到 L8 服务期 · 共 ${followItems.length} 条跟进 · 当前 ${stage.label}`}
            onClick={() => setShowJourney(true)}
          />

          {/* P1-5 客户获取证据卡 */}
          {c.acquisitionSource && (
            <AcquisitionEvidenceCard source={c.acquisitionSource} />
          )}

          {/* v1.1 AI 画像 + 健康度 双卡 */}
          <AiV11Cards customer={c} />

          <AiProfileSection customerId={cid} profile={c.aiProfileJson} />

          {/* P3-2: 客户健康度告警条 */}
          {c.currentAlertLevel && c.currentAlertLevel !== 'healthy' && (
            <HealthBadge customer={c} />
          )}
          {c.isLongCycle && (
            <div style={{
              padding: '10px 14px', marginBottom: 16,
              background: 'rgba(124,77,255,0.06)',
              border: '1px solid var(--purple)', borderRadius: 6,
              fontSize: 13, color: 'var(--text-1)',
            }}>
              🌱 <strong>长周期客户</strong> · 已加微信 {c.assignedAt ? dayjs().diff(dayjs(c.assignedAt), 'day') : '?'} 天
              <span style={{ marginLeft: 8, color: 'var(--text-3)', fontSize: 12 }}>
                养客户库重点：维系关系而非逼单
              </span>
            </div>
          )}

          {/* P3-1: 广告来源（仅有 ad_source_lead_id 的客户显示） */}
          {c.adSourceLeadId && (
            <div className="home-card" style={{ marginBottom: 16 }}>
              <div className="home-card-head">
                <div className="home-card-title">🎯 广告来源</div>
                <Link to={`/marketing/leads/${c.adSourceLeadId}`} className="home-card-action">查看原线索</Link>
              </div>
              <div style={{ padding: 18, display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr', columnGap: 12, rowGap: 6, fontSize: 13 }}>
                <span style={{ color: 'var(--text-3)' }}>平台</span><span>小红书</span>
                <span style={{ color: 'var(--text-3)' }}>首次接触</span>
                <span>{fmtMonthDayTime(c.adFirstContactAt)}</span>
                <span style={{ color: 'var(--text-3)' }}>计划</span><span>{c.adSourceCampaignName ?? '—'}</span>
                <span style={{ color: 'var(--text-3)' }}>创意</span><span>{c.adSourceCreativeName ?? '—'}</span>
                {c.adSourceNoteLink && <>
                  <span style={{ color: 'var(--text-3)' }}>笔记</span>
                  <span style={{ gridColumn: 'span 3' }}>
                    <a href={c.adSourceNoteLink} target="_blank" rel="noopener noreferrer"
                       style={{ color: 'var(--primary)', fontSize: 12 }}>{c.adSourceNoteLink}</a>
                  </span>
                </>}
              </div>
            </div>
          )}

          {/* P2-5: 阶段操作指南（按客户当前 stage + 个性化 tailored）*/}
          <div style={{ marginBottom: 16 }}>
            <StageSopCard mode="detail" initialStage={c.currentStage} customerId={cid} />
          </div>


          <div className="stats-row" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-label">当前阶段</div>
              <div className="stat-value-row" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Pill tone={stage.tone}>{stage.label}</Pill>
                {/* 软改硬(inc4)：L3+ 推进待主管审核中间态 */}
                {c.pendingStage && (
                  <Pill tone="warning">⏳ 推进待审 → {STAGE_META[c.pendingStage]?.label ?? c.pendingStage}</Pill>
                )}
              </div>
              <div className="stat-meta">{c.stageChangedAt ? `${fmtMonthDayTime(c.stageChangedAt)} 推进` : '—'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">健康度</div>
              <div className="stat-value-row">
                <span className="stat-value">{c.healthScore ?? '—'}</span>
                <span className="stat-unit">/100</span>
              </div>
              <div className="stat-meta">成交概率 {c.dealProbability ?? 0}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">已收款</div>
              <div className="stat-value-row">
                <span className="stat-value">¥{Number(c.totalPaid ?? 0).toLocaleString()}</span>
              </div>
              <div className="stat-meta">应收 ¥{Number(c.totalDue ?? 0).toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">跟进次数</div>
              <div className="stat-value-row">
                <span className="stat-value">{followItems.length}</span>
                <span className="stat-unit">次</span>
              </div>
              <div className="stat-meta">最近 {fmtMonthDayTime(c.lastFollowupAt)}</div>
            </div>
          </div>

          <div className="home-card" style={{ marginBottom: 16 }}>
            <div className="home-card-head">
              <div className="home-card-title">客户信息</div>
            </div>
            <div style={{ padding: 18 }}>
              <div className="detail-kv" style={{ gridTemplateColumns: '90px 1fr 90px 1fr', rowGap: 12 }}>
                <span className="detail-kv-label">昵称</span><span className="detail-kv-value">{c.nickname}</span>
                <span className="detail-kv-label">真实姓名</span><RealNameKv c={c} />
                <span className="detail-kv-label">性别</span><span className="detail-kv-value">{c.gender || '—'}</span>
                <span className="detail-kv-label">年龄</span><span className="detail-kv-value">{c.age || '—'}</span>
                <span className="detail-kv-label">地区</span><span className="detail-kv-value">{c.region || '—'}</span>
                <span className="detail-kv-label">学历</span><span className="detail-kv-value">{c.education || '—'}</span>
                <span className="detail-kv-label">微信号</span><span className="detail-kv-value">{c.wechatId || '—'}</span>
                <span className="detail-kv-label">手机号</span><span className="detail-kv-value">{c.phone || '—'}</span>
                <span className="detail-kv-label">来源</span><span className="detail-kv-value">{c.source}</span>
                <span className="detail-kv-label">情绪</span><span className="detail-kv-value">{c.mood || '—'}</span>
                <span className="detail-kv-label">需求</span><span className="detail-kv-value" style={{ gridColumn: 'span 3' }}>{c.intent || '—'}</span>
                <span className="detail-kv-label">备注</span><span className="detail-kv-value" style={{ gridColumn: 'span 3' }}>{c.notes || '—'}</span>
                <span className="detail-kv-label">内部编号</span><InternalCodeKv c={c} />
              </div>
            </div>
          </div>

          <ActivityTimeline customerId={cid} />

        </div>

        {/* RIGHT DETAIL PANEL */}
        <aside className="detail-panel">
          <div className="detail-header">
            <div className="detail-avatar" style={{ background: AVATAR_BG[slot] }}>
              {c.nickname?.charAt(0)}
            </div>
            <div className="detail-name-block">
              <div className="detail-name">
                {c.nickname}
                <Pill tone={status.tone}>{status.label}</Pill>
              </div>
              <div className="detail-sub">
                {[c.age && `${c.age} 岁`, c.gender, c.region].filter(Boolean).join(' · ') || c.source}
              </div>
            </div>
          </div>
          <div className="detail-tabs">
            <button className={`detail-tab ${tab === 'detail' ? 'active' : ''}`} onClick={() => setTab('detail')}>详情</button>
            <button className={`detail-tab ${tab === 'timeline' ? 'active' : ''}`} onClick={() => setTab('timeline')}>跟进历史</button>
            <button className={`detail-tab ${tab === 'business' ? 'active' : ''}`} onClick={() => setTab('business')}>合同订单</button>
            <button className={`detail-tab ${tab === 'attachments' ? 'active' : ''}`} onClick={() => setTab('attachments')}>
              附件{attachmentCount > 0 ? ` (${attachmentCount})` : ''}
            </button>
          </div>
          <div className="detail-body">
            {tab === 'detail' && (
              <>
                <div className="detail-section">
                  <div className="detail-section-title">基础信息</div>
                  <div className="detail-kv">
                    <span className="detail-kv-label">来源</span><span className="detail-kv-value">{c.source}</span>
                    <span className="detail-kv-label">阶段</span><span className="detail-kv-value">{stage.label}</span>
                    <span className="detail-kv-label">状态</span><span className="detail-kv-value">{status.label}</span>
                    <span className="detail-kv-label">健康度</span><span className="detail-kv-value">{c.healthScore ?? '—'}</span>
                    <span className="detail-kv-label">成交率</span><span className="detail-kv-value">{c.dealProbability ?? 0}%</span>
                    <span className="detail-kv-label">已收款</span><span className="detail-kv-value">¥{Number(c.totalPaid ?? 0).toLocaleString()}</span>
                    <span className="detail-kv-label">交付负责人</span><span className="detail-kv-value">{c.academicLead?.realName ?? '待分配'}</span>
                  </div>
                </div>
                {c.aiProfileJson?.summary && (
                  <div className="detail-section">
                    <AiBox title="AI 画像">
                      <AiMd text={c.aiProfileJson.summary} />
                    </AiBox>
                  </div>
                )}
                <div className="detail-section">
                  <div className="detail-section-title">最近跟进</div>
                  {recent.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>暂无</div>
                  ) : (
                    <div className="timeline">
                      {recent.map((f: any) => (
                        <div key={f.id} className="tl-item">
                          <span className="tl-dot done" />
                          <div className="tl-stage">
                            <span className="tl-stage-name">{f.stage ?? '跟进'}</span>
                            <span className="tl-stage-date">{fmtMonthDayTime(f.createdAt)}</span>
                          </div>
                          <div className="tl-desc">{(f.aiSummary || f.content || '').slice(0, 60)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {tab === 'timeline' && (
              <div className="detail-section">
                <div className="detail-section-title">L1 → L8 推进</div>
                <div className="timeline">
                  {/* 权威 10 档：L5 拆 L5a/L5b（清掉旧 'L5' 残留）。时间轴不含 L0(加微前置态) */}
                  {['L1', 'L2', 'L3', 'L4', 'L5a', 'L5b', 'L6', 'L7', 'L8'].map((st) => {
                    const ORDER = ['L1', 'L2', 'L3', 'L4', 'L5a', 'L5b', 'L6', 'L7', 'L8'];
                    const reached = ORDER.indexOf(c.currentStage) >= ORDER.indexOf(st);
                    const current = c.currentStage === st;
                    const f = followItems.find((x: any) => (x.stage ?? '') === st);
                    return (
                      <div key={st} className="tl-item">
                        <span className={`tl-dot ${current ? 'current' : reached ? 'done' : ''}`} />
                        <div className="tl-stage">
                          <span className="tl-stage-name">{STAGE_META[st]?.label ?? st}</span>
                          <span className="tl-stage-date">{f ? fmtDateShort(f.createdAt) : (reached ? '已完成' : '待开始')}</span>
                        </div>
                        {f && <div className="tl-desc">{(f.aiSummary || f.content || '').slice(0, 80)}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {tab === 'business' && (
              <>
                <div className="detail-section">
                  <div className="detail-section-title">合同</div>
                  <div className="detail-kv">
                    {/* 项3/4：审批态(pending/rejected)优先显示；否则按 esign_status（all_signed 才算已签） */}
                    <span className="detail-kv-label">合同</span><span className="detail-kv-value">{
                      c.esignStatus === 'all_signed' ? '✓ 已签'
                      : c.contractApprovalStatus === 'pending' ? '⏳ 待主管审核'
                      : c.contractApprovalStatus === 'rejected' ? `❌ 审批驳回${c.contractRejectionReason ? `（${c.contractRejectionReason}）` : ''}`
                      : c.esignStatus === 'customer_signed' ? '客户已签·待盖章'
                      : c.esignStatus === 'sent' ? '待签字'
                      : c.esignStatus === 'draft' ? '草稿'
                      : c.hasContract ? '已发起' : '未发起'
                    }</span>
                  </div>
                </div>
                {/* 第2批合页收口：原内嵌发票卡 + 收款/邮寄/资料/订单只读 KV 与左侧主列「合并流程卡」重复，
                    已收掉，业务操作统一入口在左侧流程卡（同一操作全站只一个主入口）。保留上方合同状态只读摘要。 */}
                <div className="detail-section">
                  <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6 }}>
                    收款 / 发票 / 教材 / 资料 / 订单的业务操作已并入左侧「销售流程」卡，请在左侧流程卡内办理。
                  </div>
                </div>
              </>
            )}
            {tab === 'attachments' && (
              <AttachmentsTab attachments={attachments ?? []} />
            )}
          </div>
          <div className="detail-actions">
            {/* ux6：deleted 客户隐藏右侧「新增跟进」（已无 owner、无意义） */}
            {!isDeleted && (
              <Btn kind="default" onClick={() => nav(`/follow-ups/new?customer=${cid}`)}>新增跟进</Btn>
            )}
            <Btn kind="primary" onClick={() => setShowJourney(true)}>完整旅程</Btn>
          </div>
        </aside>
      </div>

      <JourneyModal open={showJourney} customerId={cid} onClose={() => setShowJourney(false)} />
      <ChatAnalysisModal
        open={showChatAnalyze}
        customerId={cid}
        customerName={c.nickname}
        currentStage={c.currentStage}
        onClose={() => setShowChatAnalyze(false)}
        onSaved={() => {
          // 写入后刷新跟进列表 + 客户基础信息（健康度/阶段可能变了）
          qc.invalidateQueries({ queryKey: ['customer', String(cid)] });
          qc.invalidateQueries({ queryKey: ['follow-ups', cid] });
        }}
      />
      <SuggestMessageModal
        open={showSuggest}
        customerId={cid}
        customerName={c.nickname}
        onClose={() => setShowSuggest(false)}
      />
      <TransferCustomerModal
        open={showTransfer}
        customerId={cid}
        customerName={c.nickname}
        currentOwnerId={c.ownerId}
        onClose={() => setShowTransfer(false)}
      />
      <WechatProofModal
        open={showProof}
        customerId={cid}
        onClose={() => setShowProof(false)}
      />
      {/* ux6：删客户强确认——输入客户姓名(真名优先回退昵称)比对一致才放行 */}
      <Modal
        title="删除客户（高危操作）"
        open={showDeleteConfirm}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true, disabled: !(c.realName || c.nickname || '').trim() || delConfirmText.trim() !== (c.realName || c.nickname || '').trim() }}
        onCancel={() => setShowDeleteConfirm(false)}
        onOk={async () => {
          try {
            await $.delete(`/customers/${cid}`);
            message.success('已删除');
            setShowDeleteConfirm(false);
            nav('/customers');
          } catch (e: any) {
            message.error(e?.response?.data?.message ?? e?.message ?? '删除失败');
          }
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
          删除为软删（可在「已删除」tab 恢复）；名下有合同/订单/收款则不可删除。<br/>
          请输入客户姓名 <strong>{c.realName || c.nickname}</strong> 以确认删除：
        </div>
        <Input
          autoFocus
          value={delConfirmText}
          onChange={(e) => setDelConfirmText(e.target.value)}
          placeholder={c.realName || c.nickname}
          onPressEnter={(e) => e.preventDefault()}
        />
      </Modal>
    </>
  );
}

/** P1-3: 客户 AI 画像区块 —— 显示当前 profile + 手动重生成按钮
 *  自动触发由后端 `follow_up.created` 事件 + 周度 cron 完成；
 *  这里的按钮是给销售员"我刚加了一条重要跟进，想立刻看新画像"用的。 */
function AiProfileSection({ customerId, profile }: { customerId: number; profile: any }) {
  const qc = useQueryClient();
  const gen = useMutation({
    mutationFn: () => $.post(`/customers/${customerId}/generate-profile`, {}),
    onSuccess: () => {
      message.success('AI 画像已更新');
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
    },
    onError: (e: any) => {
      message.error(e?.message ?? 'AI 画像生成失败');
    },
  });
  const generatedAt = profile?.generatedAt;
  const hasProfile = !!profile?.summary;
  return (
    <div style={{ marginBottom: 16 }}>
      <AiBox
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <span>AI 客户画像</span>
            {generatedAt && (
              <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>
                {fmtRelative(generatedAt)}更新 · {profile.followUpCountWhenGenerated ?? '?'} 条跟进
              </span>
            )}
            <div style={{ flex: 1 }} />
            <a
              onClick={(e) => { e.preventDefault(); gen.mutate(); }}
              style={{
                fontSize: 12, cursor: gen.isPending ? 'wait' : 'pointer',
                color: 'var(--purple)',
              }}
            >
              {gen.isPending ? '生成中…' : hasProfile ? '重新生成' : '生成画像'}
            </a>
          </div>
        }
      >
        {hasProfile ? (
          <AiMd text={profile.summary} />
        ) : (
          <div style={{ color: 'var(--text-3)', fontSize: 12, padding: '4px 0' }}>
            该客户尚未生成画像。需至少 3 条跟进，跟进达标后系统会自动生成；也可点右上角手动触发。
          </div>
        )}
      </AiBox>
    </div>
  );
}

/** P3-2 客户健康度告警条 */
function HealthBadge({ customer }: { customer: any }) {
  const meta: Record<string, { label: string; bg: string; color: string }> = {
    warning:    { label: '⚠️ 警告',     bg: 'rgba(250,173,20,0.08)', color: '#faad14' },
    urgent:     { label: '🟠 紧急',     bg: 'rgba(250,140,22,0.08)', color: '#fa8c16' },
    death:      { label: '🔴 已逾期',   bg: 'rgba(255,77,79,0.08)',  color: 'var(--danger)' },
    dying:      { label: '💀 濒死',     bg: 'rgba(255,77,79,0.12)',  color: 'var(--danger)' },
    pool_force: { label: '⚰️ 即将进公海', bg: 'rgba(255,77,79,0.15)', color: 'var(--danger)' },
  };
  const m = meta[customer.currentAlertLevel] ?? meta.warning;
  const due = customer.nextFollowupDueAt ? dayjs(customer.nextFollowupDueAt) : null;
  const dayDiff = due ? due.diff(dayjs(), 'day') : null;
  return (
    <div style={{
      padding: '10px 14px', marginBottom: 16,
      background: m.bg, border: `1px solid ${m.color}`, borderRadius: 6,
      display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
    }}>
      <span style={{ fontWeight: 600, color: m.color }}>{m.label}</span>
      {due && (
        <span>
          {dayDiff != null && dayDiff < 0
            ? `已逾期 ${-dayDiff} 天`
            : `距下次跟进截止 ${dayDiff} 天`}
          <span style={{ marginLeft: 8, color: 'var(--text-3)', fontSize: 12 }}>
            ({fmtMonthDayTime(due)})
          </span>
        </span>
      )}
      <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>
        健康度 {customer.followupHealthScore ?? '—'}
      </span>
    </div>
  );
}

/** P2-8 客户活动时间线 —— 合并 follow-ups / 阶段变更 / 订单 / 收款 / 创建 / 分配 */
const EVENT_META: Record<string, { icon: string; label: string; color: string }> = {
  follow_up:     { icon: '💬', label: '跟进', color: 'var(--primary)' },
  stage_changed: { icon: '📊', label: '阶段', color: 'var(--purple)' },
  order_created: { icon: '📝', label: '下单', color: '#fa8c16' },
  order_approved:{ icon: '💰', label: '成交', color: 'var(--success)' },
  payment:       { icon: '💳', label: '收款', color: 'var(--success)' },
  created:       { icon: '🎯', label: '创建', color: 'var(--text-3)' },
  assigned:      { icon: '🔄', label: '分配', color: '#1677ff' },
};
const FILTER_TABS: Array<{ key: string; label: string; types: string[] }> = [
  { key: 'all',    label: '全部', types: [] },
  { key: 'fu',     label: '跟进', types: ['follow_up'] },
  { key: 'order',  label: '订单/收款', types: ['order_created', 'order_approved', 'payment'] },
  { key: 'stage',  label: '阶段变更', types: ['stage_changed'] },
];

function ActivityTimeline({ customerId }: { customerId: number }) {
  const [filter, setFilter] = useState('all');
  const { data: events } = useQuery<any[]>({
    queryKey: ['customer-timeline', customerId],
    queryFn: () => $.get(`/customers/${customerId}/timeline`),
    enabled: !!customerId,
  });
  const active = FILTER_TABS.find((t) => t.key === filter) ?? FILTER_TABS[0];
  const filtered = (events ?? []).filter((e: any) =>
    active.types.length === 0 || active.types.includes(e.type)
  );
  return (
    <div className="home-card">
      <div className="home-card-head">
        <div className="home-card-title">客户活动时间线</div>
        <Link to={`/follow-ups/new?customer=${customerId}`} className="home-card-action">新增跟进</Link>
      </div>
      <div style={{ padding: '12px 24px 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {FILTER_TABS.map((t) => {
          const cnt = t.types.length === 0
            ? (events?.length ?? 0)
            : (events ?? []).filter((e: any) => t.types.includes(e.type)).length;
          return (
            <span
              key={t.key}
              onClick={() => setFilter(t.key)}
              style={{
                padding: '3px 10px', fontSize: 12, borderRadius: 12, cursor: 'pointer',
                background: filter === t.key ? 'var(--primary)' : 'var(--bg-2)',
                color: filter === t.key ? '#fff' : 'var(--text-2)',
                border: '1px solid ' + (filter === t.key ? 'var(--primary)' : 'var(--border)'),
              }}
            >
              {t.label} {cnt > 0 && <span style={{ opacity: 0.7 }}>({cnt})</span>}
            </span>
          );
        })}
      </div>
      <div style={{ padding: '16px 24px' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
            {events?.length ? '该类型无事件' : '暂无活动'}
          </div>
        ) : (
          <div className="timeline">
            {filtered.map((e: any, i: number) => {
              const meta = EVENT_META[e.type] ?? EVENT_META.created;
              return (
                <div key={`${e.type}-${i}-${e.ts}`} className="tl-item">
                  <span className="tl-dot done" style={{ background: meta.color }} />
                  <div className="tl-stage">
                    <div className="tl-stage-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{meta.icon}</span>
                      <span style={{ fontWeight: 500 }}>{e.title}</span>
                      {e.userInfo && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                          <Avatar userId={e.userId ?? 0} src={e.userInfo.avatarUrl}
                                  name={e.userInfo.realName} size="sm" />
                          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{e.userInfo.realName}</span>
                        </span>
                      )}
                      {e.type === 'follow_up' && e.meta?.stageChanged && e.meta?.previousStage && (
                        <Pill tone="primary">{e.meta.previousStage} →</Pill>
                      )}
                    </div>
                    <div className="tl-stage-date" data-tip={fmtDateTime(e.ts)}>
                      {fmtRelative(e.ts)}
                    </div>
                  </div>
                  {e.meta?.summary && (
                    <div className="tl-desc">{e.meta.summary}</div>
                  )}
                  {!e.meta?.summary && e.meta?.content && (
                    <div className="tl-desc">{e.meta.content}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const BUSINESS_GROUPS: { key: string; label: string }[] = [
  { key: 'customers',  label: '客户分配截图' },
  { key: 'follow_ups', label: '跟进截图' },
  { key: 'contracts',  label: '合同截图' },
  { key: 'invoices',   label: '发票截图' },
  { key: 'payments',   label: '收款截图' },
  { key: 'shipments',  label: '邮寄截图' },
  { key: 'materials',  label: '学员资料' },
  { key: 'orders',     label: '订单截图' },
];

function AttachmentsTab({ attachments }: { attachments: any[] }) {
  if (attachments.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Empty description={
          <div>
            <div style={{ marginBottom: 8 }}>还没有上传截图</div>
            <Link to="/admin/data-import/screenshots" style={{ fontSize: 12 }}>
              去数据导入 → 截图批量上传 →
            </Link>
          </div>
        } />
      </div>
    );
  }
  const groups = BUSINESS_GROUPS.map((g) => ({
    ...g,
    items: attachments.filter((a) => a.businessType === g.key),
  })).filter((g) => g.items.length > 0);

  return (
    <Image.PreviewGroup>
      {groups.map((g) => (
        <div key={g.key} className="detail-section">
          <div className="detail-section-title">
            {g.label}（{g.items.length}）
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {g.items.map((a) => (
              <AttachmentThumb key={a.id} att={a} />
            ))}
          </div>
        </div>
      ))}
    </Image.PreviewGroup>
  );
}

function AttachmentThumb({ att }: { att: any }) {
  return (
    <div style={{ width: 120, fontSize: 11 }}>
      <Image
        src={att.thumbnailUrl ?? att.fileUrl}
        alt={att.fileName}
        width={120}
        height={120}
        style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }}
        preview={{ src: att.fileUrl }}
      />
      <div title={att.fileName} style={{
        marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', color: 'var(--text-2)',
      }}>
        {att.fileName}
      </div>
      <div style={{ color: 'var(--text-3)', fontSize: 10, display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
        <span>{fmtRelative(att.uploadedAt)}</span>
        {att.batchId && <span>批次 #{att.batchId}</span>}
      </div>
    </div>
  );
}

/** v1.1 AI 双卡 — 画像 + 健康度 */
function AiV11Cards({ customer }: { customer: any }) {
  const qc = useQueryClient();
  const cid = customer.id;
  const [showFeedback, setShowFeedback] = useState<'profile' | 'health' | null>(null);
  const profile = customer.aiProfileJson ?? null;
  const health = customer.healthAssessmentJson ?? null;
  // 条6：AI 健康度评估卡的颜色/标签按【分数阈值】(>=70 绿)上色，与小徽标/进度条/公海归一——
  //   原按 health.risk_level 上色、缺省永远绿(50 分阿宝也显绿、与88分同色)，是 bug。AI 的 risk_reasons 文案仍展示。
  const hl = healthLevel(customer.healthScore);

  const buildProfile = useMutation({
    mutationFn: () => $.post(`/ai/customers/${cid}/profile?force=1`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', String(cid)] }),
    onError: (e: any) => message.error(e?.message ?? 'AI 调用失败'),
  });
  const assessHealth = useMutation({
    mutationFn: () => $.post(`/ai/customers/${cid}/health?force=1`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', String(cid)] }),
    onError: (e: any) => message.error(e?.message ?? 'AI 调用失败'),
  });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
      {/* AI 客户画像 */}
      <div style={{
        padding: 14, borderRadius: 8,
        background: 'rgba(51,112,255,0.04)',
        border: '1px solid rgba(51,112,255,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 13, color: 'var(--primary)' }}>🧠 AI 客户画像</strong>
          <span style={{ marginLeft: 'auto' }}>
            <button onClick={() => buildProfile.mutate()}
                    disabled={buildProfile.isPending}
                    style={{ fontSize: 11, padding: '2px 8px', border: '1px solid var(--border)',
                             background: '#fff', borderRadius: 4, cursor: 'pointer' }}>
              {buildProfile.isPending ? '生成中…' : profile ? '🔄 刷新' : '✨ 生成画像'}
            </button>
          </span>
        </div>
        {profile && !profile.error ? (
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            {profile.personality?.type && (
              <div><strong style={{ color: 'var(--text-2)' }}>性格：</strong>{profile.personality.type}
                {profile.personality.traits && <> · {profile.personality.traits.join(' / ')}</>}
              </div>
            )}
            {profile.learning_purpose?.primary && (
              <div><strong style={{ color: 'var(--text-2)' }}>目的：</strong>{profile.learning_purpose.primary}
                {profile.learning_purpose.urgency != null && <> · 紧迫度 {profile.learning_purpose.urgency}/10</>}
              </div>
            )}
            {profile.purchase_power?.level && (
              <div><strong style={{ color: 'var(--text-2)' }}>购买力：</strong>{profile.purchase_power.level}</div>
            )}
            {profile.decision_style?.type && (
              <div><strong style={{ color: 'var(--text-2)' }}>决策：</strong>{profile.decision_style.type}</div>
            )}
            {Array.isArray(profile.pain_points_top_3) && profile.pain_points_top_3.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: 'var(--text-2)' }}>痛点：</strong>
                {profile.pain_points_top_3.map((p: any, i: number) => (
                  <span key={i} style={{ marginRight: 4, padding: '1px 6px', fontSize: 11,
                                          background: '#FFF7E6', color: 'var(--warning-text)', borderRadius: 3 }}>
                    {p}
                  </span>
                ))}
              </div>
            )}
            {profile.closing_probability != null && (
              <div style={{ marginTop: 8, padding: 8, background: 'rgba(0,180,42,0.06)', borderRadius: 4 }}>
                <strong>🎯 成交预测：</strong>{profile.closing_probability}%
                {profile.predicted_closing_days && <> · {profile.predicted_closing_days} 天</>}
                {profile.key_breakthrough_point && (
                  <div style={{ marginTop: 2, fontSize: 11 }}>突破点：{profile.key_breakthrough_point}</div>
                )}
              </div>
            )}
            {Array.isArray(profile.next_step_recommendations) && profile.next_step_recommendations.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong style={{ color: 'var(--primary)' }}>💡 下一步建议：</strong>
                <ol style={{ paddingLeft: 18, margin: '4px 0 0 0' }}>
                  {profile.next_step_recommendations.slice(0, 3).map((r: any, i: number) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {typeof r === 'string' ? r : `${r.action ?? ''}${r.timing ? ` · ${r.timing}` : ''}`}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-3)' }}>
              <button onClick={() => setShowFeedback('profile')}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-3)' }}>
                👍 准确 / 👎 不准
              </button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {profile?.error
              ? <>⚠️ {profile.error}</>
              : '点击右上「✨ 生成画像」让 AI 分析客户'}
          </div>
        )}
      </div>

      {/* AI 健康度 */}
      <div style={{
        padding: 14, borderRadius: 8,
        background: hl === 'red' ? 'rgba(245,63,63,0.06)'
                  : hl === 'yellow' ? 'rgba(255,125,0,0.06)'
                  : 'rgba(0,180,42,0.05)',
        border: '1px solid ' + (
          hl === 'red' ? 'rgba(245,63,63,0.25)'
          : hl === 'yellow' ? 'rgba(255,125,0,0.25)'
          : 'rgba(0,180,42,0.20)'
        ),
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 13 }}>🩺 AI 健康度评估</strong>
          <span style={{ marginLeft: 'auto' }}>
            <button onClick={() => assessHealth.mutate()}
                    disabled={assessHealth.isPending}
                    style={{ fontSize: 11, padding: '2px 8px', border: '1px solid var(--border)',
                             background: '#fff', borderRadius: 4, cursor: 'pointer' }}>
              {assessHealth.isPending ? '评估中…' : '🔄 重新评估'}
            </button>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 28, fontWeight: 500,
                        color: hl === 'red' ? 'var(--danger)'
                             : hl === 'yellow' ? 'var(--warning-text)'
                             : 'var(--success-text)' }}>
            {customer.healthScore ?? '—'}
          </div>
          <div>
            <div style={{ fontSize: 12,
                          color: hl === 'red' ? 'var(--danger)'
                               : hl === 'yellow' ? 'var(--warning-text)'
                               : 'var(--success-text)' }}>
              {hl === 'red' ? '🔴 危急'
               : hl === 'yellow' ? '🟡 警告'
               : '🟢 健康'}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-3)' }}>0-100 分</div>
          </div>
        </div>
        {Array.isArray(health?.risk_reasons) && health.risk_reasons.length > 0 && (
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            <strong style={{ color: 'var(--text-2)' }}>风险信号：</strong>
            <ul style={{ paddingLeft: 18, margin: '4px 0 0' }}>
              {health.risk_reasons.map((r: string, i: number) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
        {health?.predicted_closing_probability != null && (
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
            🎯 成交概率 {health.predicted_closing_probability}%
            {health.predicted_closing_days && <> · {health.predicted_closing_days} 天</>}
          </div>
        )}
        {Array.isArray(health?.rescue_recommendations) && health.rescue_recommendations.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <strong style={{ color: 'var(--primary)' }}>💡 抢救建议：</strong>
            <ol style={{ paddingLeft: 18, margin: '4px 0 0' }}>
              {health.rescue_recommendations.slice(0, 2).map((r: any, i: number) => (
                <li key={i}>{typeof r === 'string' ? r : r.action ?? JSON.stringify(r)}</li>
              ))}
            </ol>
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-3)' }}>
          <button onClick={() => setShowFeedback('health')}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-3)' }}>
            👍 准确 / 👎 不准
          </button>
        </div>
      </div>
      {showFeedback && <AiFeedbackModal module={showFeedback === 'profile' ? 'profile' : 'health'}
                                       outputRef={String(cid)}
                                       onClose={() => setShowFeedback(null)} />}
    </div>
  );
}

/** item4：真实姓名内联编辑（与锁定的昵称并排）。
 *  昵称是来源 handle、保持锁定不可改；真实姓名是「可编辑的真名」，开票抬头默认带它。
 *  权限口径对齐其它客户字段：前端不另设门控，后端 PATCH /customers/:id 的 team-scope 把关。 */
function RealNameKv({ c }: { c: any }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState<string>(c.realName ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await $.patch(`/customers/${c.id}`, { realName: val.trim() });
      message.success('真实姓名已更新');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['customer', String(c.id)] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    } catch (e: any) {
      message.error(e?.message ?? '更新失败');
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <span className="detail-kv-value">
        {c.realName || '—'}
        <a style={{ marginLeft: 10, fontSize: 12, cursor: 'pointer' }}
           onClick={() => { setVal(c.realName ?? ''); setEditing(true); }}>改</a>
      </span>
    );
  }
  return (
    <span className="detail-kv-value" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <input value={val} onChange={(e) => setVal(e.target.value)} disabled={saving}
             placeholder="真实姓名"
             style={{ padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4, minWidth: 120 }} />
      <a style={{ fontSize: 12, cursor: 'pointer' }} onClick={() => !saving && save()}>{saving ? '保存中…' : '保存'}</a>
      <a style={{ fontSize: 12, color: 'var(--text-3)', cursor: 'pointer' }} onClick={() => setEditing(false)}>取消</a>
    </span>
  );
}

/** 内部编号内联编辑（快批-2）：销售/主管在客户详情改内部编号；后端查唯一防重复。 */
function InternalCodeKv({ c }: { c: any }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState<string>(c.internalCode ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!val.trim()) { message.error('内部编号不能为空'); return; }
    setSaving(true);
    try {
      await $.patch(`/customers/${c.id}`, { internalCode: val.trim() });
      message.success('内部编号已更新');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['customer', String(c.id)] }); // batch-fix#H7：归一命名(原 ['customer-detail',..] 与详情页 queryKey 不匹配)
      qc.invalidateQueries({ queryKey: ['customers'] });
    } catch (e: any) {
      message.error(e?.message ?? '更新失败');
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <span className="detail-kv-value" style={{ gridColumn: 'span 3', fontFamily: 'monospace' }}>
        {c.internalCode || '—'}
        <a style={{ marginLeft: 10, fontSize: 12, fontFamily: 'sans-serif', cursor: 'pointer' }}
           onClick={() => { setVal(c.internalCode ?? ''); setEditing(true); }}>改</a>
      </span>
    );
  }
  return (
    <span className="detail-kv-value" style={{ gridColumn: 'span 3', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <input value={val} onChange={(e) => setVal(e.target.value)} disabled={saving}
             style={{ fontFamily: 'monospace', padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4, minWidth: 220 }} />
      <a style={{ fontSize: 12, cursor: 'pointer' }} onClick={() => !saving && save()}>{saving ? '保存中…' : '保存'}</a>
      <a style={{ fontSize: 12, color: 'var(--text-3)', cursor: 'pointer' }} onClick={() => setEditing(false)}>取消</a>
    </span>
  );
}

/** AI 反馈 modal — 通用 */
function AiFeedbackModal({ module, outputRef, onClose }:
  { module: string; outputRef: string; onClose: () => void }) {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const submit = useMutation({
    mutationFn: () => $.post('/ai/feedback', { module, output_ref: outputRef, rating, comment }),
    onSuccess: () => { message.success('感谢反馈'); onClose(); },
  });
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', padding: 20, borderRadius: 8, minWidth: 360,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ marginBottom: 12, fontWeight: 500 }}>AI {module === 'profile' ? '画像' : '健康度'} 反馈</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[1, 2, 3].map((r) => (
            <button key={r} onClick={() => setRating(r)}
                    style={{
                      flex: 1, padding: 8, fontSize: 13,
                      border: '1px solid ' + (rating === r ? 'var(--primary)' : 'var(--border)'),
                      background: rating === r ? 'var(--primary-bg)' : '#fff',
                      borderRadius: 4, cursor: 'pointer',
                    }}>
              {r === 1 ? '👎 不准' : r === 2 ? '🤔 一般' : '👍 准确'}
            </button>
          ))}
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                  rows={3} placeholder="可选 — 具体哪里不准 / 漏了什么"
                  style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }} />
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '4px 12px', border: '1px solid var(--border)', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>取消</button>
          <button onClick={() => rating && submit.mutate()} disabled={!rating || submit.isPending}
                  style={{ padding: '4px 12px', border: 'none', background: 'var(--primary)', color: '#fff', borderRadius: 4, cursor: 'pointer', opacity: rating ? 1 : 0.4 }}>
            提交
          </button>
        </div>
      </div>
    </div>
  );
}

/** P1-5 客户获取证据卡 — 显示初始分配截图 + 投流归因 */
function AcquisitionEvidenceCard({ source }: { source: any }) {
  const [showFull, setShowFull] = useState(false);
  const ad = source.adAttribution ?? {};
  const SRC_LABEL: Record<string, string> = {
    xiaohongshu: '🔴 小红书', douyin: '🎵 抖音', shipinhao: '📹 视频号',
    baidu: '🔍 百度', other: '其它',
  };
  return (
    <div style={{
      marginBottom: 16,
      padding: 16,
      background: 'var(--card-bg)',
      border: '1px solid var(--border-default)',
      borderLeft: '3px solid var(--purple)',
      borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>📸 客户获取证据</span>
        <Pill tone="purple">{SRC_LABEL[source.sourcePlatform] ?? source.sourcePlatform}</Pill>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
          获取于 {fmtDateTime(source.acquiredAt)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {source.screenshotUrl && (
          <div style={{ flex: '0 0 100px' }}>
            <img src={source.screenshotUrl} alt="获取截图"
                 onClick={() => setShowFull(true)}
                 style={{ width: 100, height: 140, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <span style={{ color: 'var(--text-3)' }}>分配给：</span>
            {source.assignedTo ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Avatar userId={source.assignedTo.id} src={source.assignedTo.avatarUrl}
                        name={source.assignedTo.realName} size="sm" />
                <span style={{ fontWeight: 500 }}>{source.assignedTo.realName}</span>
              </span>
            ) : <span>—</span>}
            {source.assignedAt && (
              <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>
                · {fmtMonthDayTime(source.assignedAt)}
              </span>
            )}
          </div>
          {(ad.source_note || ad.note_author || ad.search_keyword || ad.campaign_name) && (
            <div style={{
              padding: 10, background: 'rgba(124,77,255,0.06)', borderRadius: 6, fontSize: 12, lineHeight: 1.7,
            }}>
              <div style={{ fontWeight: 500, color: 'var(--purple-text)', marginBottom: 4 }}>📊 投流归因</div>
              {ad.source_note && <div>• 笔记：<strong>{ad.source_note}</strong></div>}
              {ad.note_author && <div>• 作者：{ad.note_author}</div>}
              {ad.search_keyword && <div>• 关键词：<strong>{ad.search_keyword}</strong></div>}
              {ad.campaign_name && <div>• 计划：{ad.campaign_name}</div>}
              {ad.creative_name && <div>• 创意：{ad.creative_name}</div>}
              {ad.account_name && <div>• 账户：{ad.account_name}</div>}
            </div>
          )}
          {source.chatSummary && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-2)' }}>
              <strong style={{ color: 'var(--text-3)' }}>对话摘要：</strong>{source.chatSummary}
            </div>
          )}
        </div>
      </div>
      {showFull && source.screenshotUrl && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
        }} onClick={() => setShowFull(false)}>
          <img src={source.screenshotUrl} alt="原图"
               style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
